import React, { useEffect, useState } from 'react'

import { useHistory } from 'react-router-dom'
import { Controller, FormContext, useForm } from 'react-hook-form'
import { reverse } from 'named-urls'
import { Box, Button, Grid, InputAdornment, TextField } from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'

import {
  AutocompleteTypeAndSearch,
  Card,
  LoadingFeedback,
  Select,
} from 'components'

import { routes } from 'Routes'
import constants from 'constants/index'
import * as service from 'service'

import brlIcon from 'images/brazilian-real-sign-solid.svg'
import helpers from 'helpers'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

import schema from './schema'

const PmcForm = ({ monitoringManagementPmc = {}, refresh, isShow = false }) => {
  const [isLoading, setIsLoading] = useState(false)

  const getDefaultValues = (pmc = {}) => ({
    title: pmc?.title || '',
    origin: pmc?.origin || '',
    justification: pmc?.justification || '',
    expectedBenefit: pmc?.expectedBenefit || '',
    plannedActions: pmc?.plannedActions || '',
    responsibleId: pmc?.responsibleId || pmc?.user || null,
    endDatePlanned: pmc?.endDatePlanned || null,
    improvementType: pmc?.improvementType || '',
    participants: pmc?.participants || pmc?.users || [],
    resourcesNeeded: pmc?.resourcesNeeded || '',
    estimatedInvestment: helpers.formatters.formatToCurrencyBrl(
      pmc?.estimatedInvestment || '0',
    ),
    status: pmc?.status || '',
    startDatePlanned: pmc?.startDatePlanned || null,
    successCriteria: pmc?.successCriteria || '',
    impactedKpiId: pmc?.impactedKpi?.id || '',
    resultObtained: pmc?.resultObtained || '',
    departmentId: pmc?.department?.id || pmc?.departmentId || '',
  })

  const history = useHistory()
  const snackbar = useSnackbar()

  const formMethods = useForm({
    validationSchema: schema,
    defaultValues: getDefaultValues(monitoringManagementPmc),
  })

  const { control, errors, handleSubmit, reset } = formMethods

  useEffect(() => {
    if (!monitoringManagementPmc?.id) return

    reset(getDefaultValues(monitoringManagementPmc))
  }, [monitoringManagementPmc?.id, reset])

  const { response: responseKpi, isLoading: isLoadingKpi } = useFetch(
    service.dponet.monitoringManagementKpi.list,
    {
      perPage: 1000,
      order: 'name ASC',
    },
  )

  const { response: responseDepartments, isLoading: isDepartmentsLoading } =
    useFetch(service.dponet.departments.get, {
      perPage: 1000,
      order: 'name ASC',
      minimal: true,
    })

  const responseUsers = async (inputValue) => {
    try {
      const response = await service.dponet.users.get({
        name: inputValue,
        perPage: 10000000,
        minimal: true,
        order: 'name ASC',
      })

      return response?.data?.users || []
    } catch {
      return []
    }
  }

  const impactedKpiId = responseKpi?.data?.monitoringManagementKpis || []

  const departments = responseDepartments?.data?.departments || []

  const isEdit = !!monitoringManagementPmc?.id

  const { IMPROVEMENT_TYPE_OPTIONS, ORIGIN_OPTIONS, STATUS_OPTIONS } =
    constants.monitoringManagementPmc

  const handleGoBack = () => history.push(routes.pmc.all)
  const normalizeParticipants = (participants = []) => {
    return participants
      .map((participant) => participant?.id || participant)
      .filter(Boolean)
  }

  const onSubmit = async (data) => {
    setIsLoading(true)

    try {
      const endDate = helpers.formatters.toISOStringVerify(data?.endDatePlanned)
      const startDate = helpers.formatters.toISOStringVerify(
        data?.startDatePlanned,
      )

      const payload = {
        ...data,
        responsibleId: data?.responsibleId?.id || data?.responsibleId,
        participants: normalizeParticipants(data?.participants),
        endDatePlanned: endDate,
        startDatePlanned: startDate,
        estimatedInvestment: data?.estimatedInvestment
          ? helpers.formatters.money.convert(data.estimatedInvestment)
          : data?.estimatedInvestment,
      }

      if (isEdit) {
        await service.dponet.monitoringManagementPmc.update({
          monitoringManagementPmcId: monitoringManagementPmc?.id,
          ...payload,
        })

        snackbar.open({
          message: 'PMC atualizado com sucesso',
          variant: 'success',
        })

        refresh()
      } else {
        const response = await service.dponet.monitoringManagementPmc.create(
          payload,
        )
        const monitoringManagementPmcResponse =
          response?.data?.monitoringManagementPmc
        snackbar.open({
          message: 'PMC criado com sucesso',
          variant: 'success',
        })
        history.push(
          reverse(routes.pmc.edit, {
            monitoringManagementPmcId: monitoringManagementPmcResponse?.id,
          }),
        )
      }
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
      reset(data)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <FormContext {...formMethods}>
      <LoadingFeedback open={isLoading || isLoadingKpi} />
      <form id="pmc-form" onSubmit={handleSubmit(onSubmit)}>
        <Card title="Informações">
          <Grid container spacing={2}>
            <Grid xs={12} item>
              <Controller
                as={
                  <TextField
                    label="Título *"
                    fullWidth
                    disabled={isShow}
                    variant="outlined"
                    error={!!errors.title}
                    helperText={errors?.title?.message}
                  />
                }
                control={control}
                name="title"
                defaultValue=""
              />
            </Grid>

            <Grid xs={12} sm={6} item>
              <Controller
                as={
                  <Select
                    label="Origem *"
                    fieldValue="value"
                    fieldName="name"
                    items={ORIGIN_OPTIONS}
                    disabled={isShow}
                    error={!!errors?.origin}
                    helperText={errors?.origin?.message}
                  />
                }
                name="origin"
                control={control}
                defaultValue=""
                onChange={([event]) => event.target.value}
              />
            </Grid>

            <Grid xs={12} sm={6} item>
              <AutocompleteTypeAndSearch
                textField={{
                  label: 'Selecione o responsável pelo PMC *',
                  variant: 'outlined',
                  error: !!errors?.responsibleId,
                  helperText: errors?.responsibleId?.message,
                }}
                requestToLoadOptions={responseUsers}
                name="responsibleId"
                disabled={isShow}
              />
            </Grid>

            <Grid xs={12} sm={6} item>
              <Controller
                as={
                  <Select
                    label="Departamento *"
                    fieldValue="id"
                    fieldName="name"
                    items={departments}
                    disabled={isShow}
                    error={!!errors.departmentId}
                    helperText={errors?.departmentId?.message}
                  />
                }
                name="departmentId"
                control={control}
                defaultValue=""
                onChange={([event]) => event.target.value}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                as={
                  <Select
                    label="Status *"
                    fieldValue="value"
                    fieldName="name"
                    items={STATUS_OPTIONS}
                    disabled={isShow}
                    error={!!errors?.status}
                    helperText={errors?.status?.message}
                  />
                }
                name="status"
                control={control}
                defaultValue=""
                onChange={([event]) => event.target.value}
              />
            </Grid>

            <Grid xs={12} item>
              <Controller
                as={
                  <TextField
                    label="Justificativa *"
                    fullWidth
                    variant="outlined"
                    multiline
                    minRows={3}
                    disabled={isShow}
                    error={!!errors.justification}
                    helperText={errors?.justification?.message}
                  />
                }
                control={control}
                name="justification"
                defaultValue=""
              />
            </Grid>

            <Grid xs={12} item>
              <Controller
                as={
                  <TextField
                    label="Benefício Esperado *"
                    fullWidth
                    variant="outlined"
                    multiline
                    minRows={3}
                    disabled={isShow}
                    error={!!errors.expectedBenefit}
                    helperText={errors?.expectedBenefit?.message}
                  />
                }
                control={control}
                name="expectedBenefit"
                defaultValue=""
              />
            </Grid>

            <Grid xs={12} item>
              <Controller
                as={
                  <TextField
                    label="Ações Planejadas *"
                    fullWidth
                    variant="outlined"
                    multiline
                    minRows={3}
                    disabled={isShow}
                    error={!!errors.plannedActions}
                    helperText={errors?.plannedActions?.message}
                  />
                }
                control={control}
                name="plannedActions"
                defaultValue=""
              />
            </Grid>

            <Grid xs={12} sm={6} item>
              <Controller
                as={
                  <KeyboardDatePicker
                    fullWidth
                    format="DD/MM/yyyy"
                    label="Data Fim Prevista *"
                    helperText={errors?.endDatePlanned?.message}
                    error={!!errors.endDatePlanned}
                    inputVariant="outlined"
                    onChange={(newValue) => ({ value: newValue })}
                    disabled={isShow}
                  />
                }
                control={control}
                defaultValue={null}
                name="endDatePlanned"
                mode="onChange"
              />
            </Grid>
            <Grid xs={12} sm={6} item>
              <Controller
                as={
                  <KeyboardDatePicker
                    fullWidth
                    format="DD/MM/yyyy"
                    label="Data Início Prevista"
                    helperText={errors?.startDatePlanned?.message}
                    error={!!errors.startDatePlanned}
                    inputVariant="outlined"
                    onChange={(newValue) => ({ value: newValue })}
                    disabled={isShow}
                  />
                }
                control={control}
                defaultValue={null}
                name="startDatePlanned"
                mode="onChange"
              />
            </Grid>
            <Grid xs={12} sm={6} item>
              <Controller
                as={
                  <TextField
                    label="Investimento Estimado (R$)"
                    type="text"
                    variant="outlined"
                    error={!!errors.estimatedInvestment}
                    helperText={errors?.estimatedInvestment?.message}
                    fullWidth
                    disabled={isShow}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <img src={brlIcon} alt="Logo" width={15} />
                        </InputAdornment>
                      ),
                    }}
                  />
                }
                control={control}
                name="estimatedInvestment"
                defaultValue=""
                mode="onChange"
                onChange={([event]) => {
                  return helpers.formatters.currencyInput(
                    event.target.value || '0',
                  )
                }}
              />
            </Grid>
            <Grid xs={12} sm={6} item>
              <Controller
                as={
                  <Select
                    label="Tipo de Melhoria"
                    fieldValue="value"
                    fieldName="name"
                    items={IMPROVEMENT_TYPE_OPTIONS}
                    disabled={isShow}
                    error={!!errors?.improvementType}
                    helperText={errors?.improvementType?.message}
                  />
                }
                name="improvementType"
                control={control}
                defaultValue=""
                onChange={([event]) => event.target.value}
              />
            </Grid>

            <Grid xs={12} sm={6} item>
              <AutocompleteTypeAndSearch
                textField={{ label: 'Participantes', variant: 'outlined' }}
                requestToLoadOptions={responseUsers}
                name="participants"
                multiple
                disabled={isShow}
              />
            </Grid>

            <Grid xs={12} sm={6} item>
              <Controller
                as={
                  <TextField
                    label="Recursos Necessários"
                    fullWidth
                    variant="outlined"
                    disabled={isShow}
                    error={!!errors.resources}
                    helperText={errors?.resources?.message}
                  />
                }
                control={control}
                name="resourcesNeeded"
                defaultValue=""
              />
            </Grid>

            <Grid xs={12} item>
              <Controller
                as={
                  <TextField
                    label="Critério de Sucesso"
                    fullWidth
                    variant="outlined"
                    disabled={isShow}
                    error={!!errors.successCriteria}
                    helperText={errors?.successCriteria?.message}
                  />
                }
                control={control}
                name="successCriteria"
                defaultValue=""
              />
            </Grid>

            <Grid xs={12} item>
              <Controller
                as={
                  <Select
                    label="KPI Impactado"
                    fieldValue="id"
                    fieldName="name"
                    items={impactedKpiId}
                    disabled={isShow}
                    error={!!errors?.impactedKpiId}
                    helperText={errors?.impactedKpiId?.message}
                    // TODO: preencher opções de KPI
                  />
                }
                name="impactedKpiId"
                control={control}
                defaultValue=""
                onChange={([event]) => event.target.value}
              />
            </Grid>
            <Grid xs={12} item>
              <Controller
                as={
                  <TextField
                    label="Resultado Obtido"
                    fullWidth
                    multiline
                    minRows={3}
                    variant="outlined"
                    disabled={isShow}
                    error={!!errors.resultObtained}
                    helperText={errors?.resultObtained?.message}
                  />
                }
                control={control}
                name="resultObtained"
                defaultValue=""
              />
            </Grid>
          </Grid>
        </Card>
        <Box ml={2} mt={2} display="flex" justifyContent="flex-end">
          <Box pr={1}>
            <Button type="button" variant="outlined" onClick={handleGoBack}>
              Voltar
            </Button>
          </Box>
          {!isShow && (
            <Button
              form="pmc-form"
              type="submit"
              color="primary"
              variant="contained"
            >
              {isEdit ? 'Salvar' : 'Criar'}
            </Button>
          )}
        </Box>
      </form>
    </FormContext>
  )
}

export default PmcForm
